// Copyright (c) 2021 JCA Electronics , Winnipeg, MB. All rights reserved.

import { SinglePathMission } from '@/flightpath-components/models/SinglePathMission';
import { SimplePoint } from '@/flightpath-components/models/SimplePoint';
import { Point } from '@/flightpath-components/models/Point';
import RenderColors from '@/flightpath-components/services/colors';

export class RenderHelper {
  static getBounds(path: google.maps.MVCArray<google.maps.LatLng>): google.maps.LatLngBounds {
    const bounds = new google.maps.LatLngBounds();
    path.forEach(point => bounds.extend(point));
    return bounds;
  }

  static getFieldPolygonOptionsFromPoint(path: Point[]): google.maps.PolygonOptions {
    const points = path.map(val => {
      return { lat: val.Item1, lng: val.Item2 };
    });
    return this.getFieldPolygonOptions(points);
  }

  static getFieldPolygonOptionsFromMission(missionPlan: SinglePathMission): google.maps.PolygonOptions {
    const points = missionPlan.FieldBoundary.DrawPoints.map(val => {
      return { lat: val.Latitude, lng: val.Longitude };
    });
    return this.getFieldPolygonOptions(points);
  }

  static getFieldPolygonOptions(path: google.maps.LatLngLiteral[]): google.maps.PolygonOptions {
    return {
      paths: [path],
      strokeColor: RenderColors.Field,
      fillColor: 'transparent',
      strokeOpacity: 0.8,
      strokeWeight: 3
    };
  }

  static getWorkAreaPolygonOptionsFromMission(missionPlan: SinglePathMission): google.maps.PolygonOptions {
    let workArea: google.maps.LatLngLiteral[] = [];
    if (missionPlan != null && missionPlan.WorkAreas != undefined && missionPlan.WorkAreas[0] != undefined) {
      workArea = missionPlan.WorkAreas[0].map(val => {
        return { lat: val.Lat, lng: val.Long };
      });
    }

    return this.getWorkAreaPolygonOptions(workArea);
  }
  static getCropAreaPolygonOptionsFromMission(missionPlan: SinglePathMission): google.maps.PolygonOptions {
    const options = this.getCropAreaPolygonOptions([]);
    options.paths = (missionPlan?.CropAreas ?? []).map(area => area.map(val => ({ lat: val.Lat, lng: val.Long })));
    return options;
  }

  static getWorkAreaPolygonOptionsFromSimplePoint(path: SimplePoint[]): google.maps.PolylineOptions {
    return this.getWorkAreaPolygonOptions(
      path.map(val => {
        return { lat: val.Lat, lng: val.Long };
      })
    );
  }

  static getWorkAreaPolygonOptions(workArea: google.maps.LatLngLiteral[]): google.maps.PolygonOptions {
    return {
      paths: [workArea],
      strokeColor: 'transparent',
      fillColor: RenderColors.WorkArea,
      strokeOpacity: 0.8,
      strokeWeight: 3
    };
  }

  static getCropAreaPolygonOptions(cropArea: google.maps.LatLngLiteral[]): google.maps.PolygonOptions {
    return {
      paths: [cropArea],
      strokeColor: 'transparent',
      fillColor: RenderColors.CropArea,
      strokeOpacity: 0.8,
      strokeWeight: 3
    };
  }

  static getPathPolylineOptionsFromMission(missionPlan: SinglePathMission): google.maps.PolylineOptions {
    let path: google.maps.LatLngLiteral[] = [];
    if (missionPlan != null && missionPlan.Path != undefined) {
      path = missionPlan.Path.map(val => {
        return { lat: val.Lat, lng: val.Long };
      });
    }

    return this.getPathPolylineOptions(path);
  }

  static getPathPolylineOptionsFromSimplePoint(path: SimplePoint[]): google.maps.PolylineOptions {
    return this.getPathPolylineOptions(
      path.map(val => {
        return { lat: val.Lat, lng: val.Long };
      })
    );
  }

  static getPathPolylineOptions(path: google.maps.LatLngLiteral[]): google.maps.PolylineOptions {
    return {
      path: path,
      strokeColor: RenderColors.PlannedPath,
      strokeOpacity: 0.8,
      strokeWeight: 3
    };
  }

  static clearFields(polygons: google.maps.PolygonOptions[]) {
    return polygons.filter(x => x.strokeColor != RenderColors.Field);
  }

  static clearPaths(polyLines: google.maps.PolylineOptions[]) {
    return polyLines.filter(x => x.strokeColor != RenderColors.PlannedPath);
  }

  static clearWorkAreas(polygons: google.maps.PolygonOptions[]) {
    return polygons.filter(x => x.fillColor != RenderColors.WorkArea);
  }

  static clearCropAreas(polygons: google.maps.PolygonOptions[]) {
    return polygons.filter(x => x.fillColor != RenderColors.CropArea);
  }

  static createStartMakerWithLabel(line: google.maps.Polyline, edit = false): google.maps.Marker {
    const startLabelPosition = new google.maps.Point(36, -8);
    return new google.maps.Marker({
      position: line.getPath().getAt(0),
      icon: this.createSvg('M12,8A4,4 0 0,0 8,12A4,4 0 0,0 12,16A4,4 0 0,0 16,12A4,4 0 0,0 12,8Z', startLabelPosition, edit), // mdi-circle-medium svg
      label: this.createMarkerLabel('Start')
    });
  }

  static createStopMakerWithLabel(line: google.maps.Polyline, edit = false): google.maps.Marker {
    const stopLabelPosition = new google.maps.Point(32, -8);
    return new google.maps.Marker({
      position: line.getPath().getAt(line.getPath().getLength() - 1),
      icon: this.createSvg('M18,18H6V6H18V18Z', stopLabelPosition, edit), // mdi-stop svg
      label: this.createMarkerLabel('Stop')
    });
  }

  private static createMarkerLabel(text: string): google.maps.ReadonlyMarkerLabel {
    return {
      text: text.split('').join(' '),
      color: RenderColors.PlannedPath,
      fontSize: '16px',
      fontFamily: 'Roboto'
    };
  }

  private static createSvg(path: string, labelPosition: google.maps.Point, edit: boolean): google.maps.ReadonlySymbol {
    const centeredPoint = new google.maps.Point(12, 12);
    const highPoint = new google.maps.Point(12, 32);

    return {
      path: path,
      fillColor: RenderColors.PlannedPath,
      fillOpacity: 1,
      strokeWeight: 0,
      rotation: 0,
      scale: 0.8,
      anchor: edit ? highPoint : centeredPoint,
      labelOrigin: labelPosition
    };
  }
}
